/**
 * Row — horizontal layout primitive (the deliberate departure from
 * stack-first default).
 *
 * Children flow left-to-right with a uniform `gap` between them. Use
 * Row when horizontal placement is the intent: KPI grid, toolbar, an
 * identity-block paired with actions, etc.
 *
 * Row supports `reflowAt` — at viewport widths narrower than this, the
 * row collapses to a vertical stack (`flex-direction: column`). That is
 * how stack-first survives narrow viewports: every Row degrades cleanly
 * to a Stack-like flow when there isn't enough horizontal room.
 *
 * Geometry:
 *   - gap from spacing tokens. Default `m`.
 *   - alignY (cross-axis vertical): start / center / end / stretch / baseline. Default `center`.
 *   - justify: start / center / end / between / around / evenly. Default `start`.
 *   - wrap: nowrap (default) / wrap.
 *   - reflowAt: media-query breakpoint at which row collapses to column.
 *     Values: never (default) / sm (≤480) / md (≤768) / lg (≤1024).
 *
 * @format
 */

import {forwardRef, type ElementType, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {SPACING_TOKEN_NAMES, spacingPx, type SpacingTokenName} from '../spacing';

export const ROW_ALIGN_Y = ['center', 'start', 'end', 'stretch', 'baseline'] as const;
export type RowAlignY = (typeof ROW_ALIGN_Y)[number];

export const ROW_JUSTIFY = ['start', 'center', 'end', 'between', 'around', 'evenly'] as const;
export type RowJustify = (typeof ROW_JUSTIFY)[number];

export const ROW_WRAP = ['nowrap', 'wrap'] as const;
export type RowWrap = (typeof ROW_WRAP)[number];

export const ROW_REFLOW_AT = ['never', 'sm', 'md', 'lg'] as const;
export type RowReflowAt = (typeof ROW_REFLOW_AT)[number];

export const ROW_WIDTH = ['fill', 'hug'] as const;
export type RowWidth = (typeof ROW_WIDTH)[number];

/**
 * Пороги переворота ряда в столбец (max-width медиа-запросы).
 *
 * Экспортируются, потому что содержимое ряда иногда обязано знать о
 * перевороте: у колонок с заданным `flex-basis` после смены направления
 * базис начинает работать высотой, и её нужно сбросить тем же порогом,
 * которым переворачивается сам ряд. Второй порог в компоненте-потребителе
 * рано или поздно разъедется с этим.
 */
export const ROW_REFLOW_PX: Record<Exclude<RowReflowAt, 'never'>, number> = {
  sm: 480,
  md: 768,
  lg: 1024,
};

export interface RowProps {
  /** Gap between children, from spacing tokens. Default `'m'`. */
  gap?: SpacingTokenName;
  /** Vertical alignment of children. Default `'center'`. */
  alignY?: RowAlignY;
  /** Horizontal distribution. Default `'start'`. */
  justify?: RowJustify;
  /** Wrap behaviour when content overflows. Default `'nowrap'`. */
  wrap?: RowWrap;
  /** Viewport width below which row collapses to a vertical stack. Default `'never'`. */
  reflowAt?: RowReflowAt;
  /**
   * Как ряд ведёт себя при нехватке места. `fill` (по умолчанию) — отдаёт
   * ширину соседям, как любой флекс-элемент. `hug` — держит ширину своего
   * содержимого.
   *
   * `hug` нужен ряду, содержимое которого нельзя ужать без потери: пилюли
   * и числа не переносятся по словам, и вместо переноса ряд отдал бы их
   * многоточию. Соседний `Stack` по контракту занимает 100 % ширины, так
   * что сжимается именно такой ряд.
   */
  width?: RowWidth;
  as?: ElementType;
  className?: string;
  children?: ReactNode;
}

const ALIGN_Y_MAP: Record<RowAlignY, string> = {
  center: 'center',
  start: 'flex-start',
  end: 'flex-end',
  stretch: 'stretch',
  baseline: 'baseline',
};

const JUSTIFY_MAP: Record<RowJustify, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
  evenly: 'space-evenly',
};

const Root = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $gap: SpacingTokenName;
  $alignY: RowAlignY;
  $justify: RowJustify;
  $wrap: RowWrap;
  $reflowAt: RowReflowAt;
  $width: RowWidth;
}>(({$gap, $alignY, $justify, $wrap, $reflowAt, $width}) => {
  const base = {
    display: 'flex',
    flexDirection: 'row',
    gap: spacingPx($gap),
    alignItems: ALIGN_Y_MAP[$alignY],
    justifyContent: JUSTIFY_MAP[$justify],
    flexWrap: $wrap,
    minWidth: 0,
    ...($width === 'hug' ? {flexShrink: 0} : null),
  } as const;
  if ($reflowAt === 'never') return base;
  return {
    ...base,
    [`@media (max-width: ${ROW_REFLOW_PX[$reflowAt]}px)`]: {
      flexDirection: 'column',
      // Cross-axis becomes vertical when stacked; align children to the
      // start so they don't get unexpectedly stretched / centered.
      alignItems: 'flex-start',
    },
  };
});

export const Row = forwardRef<HTMLDivElement, RowProps>(function Row(
  {
    gap = 'm',
    alignY = 'center',
    justify = 'start',
    wrap = 'nowrap',
    reflowAt = 'never',
    width = 'fill',
    as,
    className,
    children,
  },
  ref,
) {
  return (
    <Root
      ref={ref}
      as={as}
      $gap={gap}
      $alignY={alignY}
      $justify={justify}
      $wrap={wrap}
      $reflowAt={reflowAt}
      $width={width}
      className={className}
    >
      {children}
    </Root>
  );
});

export {SPACING_TOKEN_NAMES as ROW_GAP_TOKENS};
